import {
  QueryClient,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "@/utils/axios";
import { parseStatusFilters } from "@/utils/filters";
import { adaptivePollingInterval } from "@/utils/polling";
import type {
  FormalityRequestType,
  FormalityRequestInsolvencyType,
} from "@/types/entities/formality-request";
import type {
  FormalityRequestStatus,
  GetFormalityRequestFilters,
} from "./types";

const FILTER_STATUS = "filter[status]" as const;
const FILTER_IS_EXPRESS = "filter[is_express]" as const;

export const useGetFormalityRequests = (
  props: GetFormalityRequestFilters,
  refreshInterval = 0,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["formaly-requests", props],
    queryFn: async () => {
      const {
        group,
        assigned,
        [FILTER_STATUS]: filterStatus,
        [FILTER_IS_EXPRESS]: filterIsExpress,
        ...rest
      } = props;

      const { statusFilter, suspensionStatusFilter } =
        parseStatusFilters(filterStatus);

      const response = await axios.get("/admin/v1/formality-request", {
        params: {
          ...rest,
          ...(group ? { "filter[group]": group } : {}),
          ...(assigned ? { "filter[assigned]": assigned } : {}),
          ...(filterIsExpress ? { "filter[is_express]": filterIsExpress } : {}),
          ...statusFilter,
          ...suspensionStatusFilter,
          relationships:
            "process,assignedTo,flowExecution.flow,groups,currentNode,flowExecution.activeNodeExecution.revision.corrections",
        },
      });
      return response.data;
    },
    ...(refreshInterval > 0 ? { refetchInterval: refreshInterval } : {}),
    enabled, // Solo ejecutar si enabled es true
  });
};

const getFormalityRequest = async (
  id: string,
): Promise<FormalityRequestType> => {
  const response = await axios.get(`/admin/v1/formality-request/${id}`, {
    params: {
      relationships:
        "process,assignedTo,process.tags,groups,flowExecution,applicant,flowExecution.activeNodeExecution.revision.corrections,resolution,resolution.files,notificationInterventions",
    },
  });
  return response.data.data;
};

export const useGetFormalityRequestDetail = (
  id: string,
  disableInterval = false,
) => {
  return useQuery({
    queryKey: ["formaly-request", id],
    queryFn: async () => {
      return await getFormalityRequest(id);
    },
    enabled: !!id, // Solo ejecutar si hay id
    refetchInterval: disableInterval ? 0 : adaptivePollingInterval,
  });
};

export const useGetFormalityRequestLogs = (
  id: string,
  params?: {
    page?: number;
    per_page?: number;
    order_by?: string;
    grouped?: number;
  },
) => {
  return useQuery({
    queryKey: ["formaly-request-logs", id, params],
    queryFn: async () => {
      const response = await axios.get(
        `/admin/v1/formality-request/${id}/logs`,
        {
          params: {
            page: params?.page ?? 1,
            per_page: params?.per_page ?? 15,
            order_by: params?.order_by ?? "!created_at",
            grouped: params?.grouped ?? 0,
          },
        },
      );
      return response.data;
    },
    enabled: !!id, // Solo ejecutar si hay id
    refetchInterval: adaptivePollingInterval,
  });
};

export const useDownloadFormalityRequestLogs = (id: string) => {
  return useMutation({
    mutationKey: ["formality-request-logs-download"],
    mutationFn: async () => {
      const filename = `logs-${id}`;
      const { data } = await axios.post(
        `/admin/v1/formality-request/${id}/logs/download`,
        { filename },
        {
          responseType: "blob",
          headers: {
            Accept: "text/csv",
            "Content-Type": "application/json",
          },
        },
      );

      const blob = new Blob([data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = globalThis.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        filename.endsWith(".csv") ? filename : `${filename}.csv`,
      );
      document.body.appendChild(link);
      link.click();

      link.remove();
      globalThis.URL.revokeObjectURL(url);
    },
  });
};

export const createFormalityRequestBreadcrumb = (
  queryClient: QueryClient,
  id: string,
) => {
  return async () => {
    try {
      const formality = await queryClient.ensureQueryData({
        queryKey: ["formaly-request", id],
        queryFn: async () => {
          const response = await getFormalityRequest(id);
          return response;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
      return {
        data: {
          name: formality.code,
        },
      };
    } catch (error) {
      console.error("Error fetching role for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};

export const useUpdateFormalityRequestAssignee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["update-formality-request-assignee"],
    mutationFn: async ({
      id,
      assignments,
      reason_text,
      reason_id,
    }: {
      id: string;
      assignments: Array<{ group_id: string; assigned_to: string }>;
      reason_text: string;
      reason_id: string;
    }) => {
      const response = await axios.put(
        `/admin/v1/formality-request/${id}/groups/reassign`,
        {
          assignments,
          reason_text,
          reason_id,
        },
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["formaly-requests"] });
      queryClient.invalidateQueries({
        queryKey: ["formaly-request", variables.id],
      });
    },
  });
};

export const useGetFormalityRequestInsolvencyStatus = (id: string) => {
  return useQuery({
    queryKey: ["formality-request-insolvency-status", id],
    queryFn: async (): Promise<FormalityRequestInsolvencyType> => {
      const response = await axios.get(
        `/admin/v1/formality-request/${id}/insolvency-validations`,
      );
      return response?.data?.data;
    },
    enabled: !!id,
  });
};

export const useGetFormalityRequestStatus = (
  id: string,
  enabled: boolean = true,
  refreshInterval: number = 0,
) => {
  return useQuery({
    queryKey: ["formality-request-status", id],
    queryFn: async (): Promise<FormalityRequestStatus> => {
      const response = await axios.get(
        `/admin/v1/formality-request/${id}/flow-status`,
      );
      return response?.data?.data;
    },
    enabled: !!id && enabled,
    ...(refreshInterval > 0
      ? { refetchInterval: adaptivePollingInterval }
      : {}),
  });
};
